import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { Board } from '@cortex/domain-org-issues';
import type { NextAction, Post, Series } from '@cortex/domain-team-meeting';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import { Modal } from '@cortex/web-design-system/components/ui/Modal';
import { useTriageTeamMeetingPost } from '../../../hooks/useTeamMeetings.js';
import type { OrgIssuePeople } from '../../../lib/api/org-issues.js';
import type { PostView } from '../../../lib/api/team-meetings.js';
import { toBoardLayout } from '../../../lib/team-meeting-board.js';
import { toCategoryColumns, type CategoryColumn } from '../../../lib/team-meetings.js';
import { BoardCanvas } from './BoardCanvas.js';
import { BoardViewport } from './BoardViewport.js';
import { CloseWithNoteModal, ConvertToIssueModal, ConvertToRequestModal } from './ConvertModals.js';
import { PostCard } from './PostCard.js';
import { PostComposer } from './PostComposer.js';
import { StickyNote } from './StickyNote.js';

/** 開いているネクストアクションのポップアップ。 @graph-connects none */
type Deciding = { kind: 'request' | 'issue' | 'close'; post: Post } | null;

/** 記録に失敗した棚卸し（起票は済んでいるので、記録だけやり直せるように残す）。 @graph-connects none */
interface FailedTriage {
  post: Post;
  nextAction: NextAction;
  message: string;
}

/**
 * 週次MTG の本体。まだ棚卸ししていない投稿（いつ書いたものでも全部）を FigJam の枠と同じ配置の
 * ボードに付箋で貼り、各区分の最後に「付箋を貼る」を置く。付箋を押すと開き、そこでいいね・議論メモ・
 * 書き換えと、リーダー以上は 1 件ずつネクストアクション（チーム課題にする・改善要望にする・
 * 対応を書いてクローズ）を決める。決めた投稿はこの回で棚卸ししたものとしてボードの下に並ぶ。
 *
 * チーム課題・改善要望は「起票 → 棚卸しの記録」の 2 書き込みになる。記録だけ失敗したときは、
 * 起票をやり直さずに記録だけやり直せるよう、決めたことを残して出す。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 週次MTG でまだ棚卸ししていない投稿を FigJam と同じ配置のボードに付箋で貼り、開いた付箋でリーダー以上がネクストアクションを決めて棚卸しし、この回で棚卸しした投稿を並べる
 * @graph-connects toCategoryColumns [calls] 区分ごとの列
 * @graph-connects toBoardLayout [calls] 区分のボード上の置き場
 * @graph-connects BoardCanvas [calls] ボード
 * @graph-connects BoardViewport [calls] ボードの拡大・縮小・移動
 * @graph-connects StickyNote [calls] 付箋 1 枚
 * @graph-connects PostCard [calls] 開いた付箋
 * @graph-connects PostComposer [calls] 付箋を貼る
 * @graph-connects ConvertToRequestModal [calls] 改善要望にする
 * @graph-connects ConvertToIssueModal [calls] チーム課題にする
 * @graph-connects CloseWithNoteModal [calls] 対応を書いてクローズ
 * @graph-connects useTriageTeamMeetingPost [calls] 棚卸しの記録
 */
export function MeetingBoard({
  series,
  meetingId,
  openPosts,
  triagedPosts,
  people,
  viewerId,
  canPost,
  defaultBoard,
}: {
  series: Series;
  /** 棚卸しする開催回（起票の起点に入れる） */
  meetingId: string;
  openPosts: readonly PostView[];
  triagedPosts: readonly PostView[];
  people: OrgIssuePeople;
  viewerId: string | null;
  canPost: boolean;
  /** 起票の先（シリーズの既定のボード）。無い・見られなければ `null` */
  defaultBoard: Board | null;
}): ReactNode {
  const { t } = useTranslation();
  const triage = useTriageTeamMeetingPost();
  const [deciding, setDeciding] = useState<Deciding>(null);
  const [failed, setFailed] = useState<FailedTriage | null>(null);
  const [openedId, setOpenedId] = useState<string | null>(null);
  const layout = toBoardLayout(toCategoryColumns(series.categories, openPosts));
  const opened = [...openPosts, ...triagedPosts].find((v) => v.post.id === openedId);
  const categoryLabel = (id: string) => series.categories.find((c) => c.id === id)?.label ?? id;
  const close = () => {
    setDeciding(null);
  };
  const record = (post: Post, nextAction: NextAction) => {
    setFailed(null);
    triage.mutate(
      { ref: { seriesId: post.seriesId, postId: post.id }, nextAction },
      {
        onError: (error) => {
          setFailed({ post, nextAction, message: error.message });
        },
      },
    );
  };
  // ネクストアクションのポップアップを開くときは、開いた付箋を閉じて入れ替える
  const decide = (kind: NonNullable<Deciding>['kind'], post: Post) => {
    setOpenedId(null);
    setDeciding({ kind, post });
  };
  const handlersFor = (post: Post) => ({
    toRequest: () => {
      decide('request', post);
    },
    toIssue:
      defaultBoard === null
        ? null
        : () => {
            decide('issue', post);
          },
    close: () => {
      decide('close', post);
    },
  });
  const sticky = (view: PostView) => (
    <StickyNote
      key={view.post.id}
      view={view}
      people={people}
      onOpen={() => {
        setOpenedId(view.post.id);
      }}
    />
  );
  const renderZone = ({ category, posts }: CategoryColumn) => (
    <>
      {posts.map(sticky)}
      {canPost && <PostComposer seriesId={series.id} category={category.id} viewerId={viewerId} />}
    </>
  );
  const modalProps =
    deciding === null
      ? null
      : {
          series,
          post: deciding.post,
          meetingId,
          onDecided: (nextAction: NextAction) => {
            record(deciding.post, nextAction);
          },
          onClose: close,
        };

  return (
    <>
      {failed !== null && (
        <div
          className="flex flex-wrap items-center gap-2 rounded border border-[var(--color-error)] p-2 text-sm"
          role="alert"
        >
          <span className="whitespace-pre-wrap text-[var(--color-error)]">
            {t('teamMeetings.triage.recordFailed', { message: failed.message })}
          </span>
          <Button
            type="button"
            size="sm"
            loading={triage.isPending}
            onClick={() => {
              record(failed.post, failed.nextAction);
            }}
          >
            {t('teamMeetings.triage.retry')}
          </Button>
        </div>
      )}

      <section className="space-y-2" aria-label={t('teamMeetings.board.openHeading')}>
        <h2 className="text-sm font-semibold text-[var(--color-text-primary)]">
          {t('teamMeetings.board.openHeading')}
          <span className="ml-1 text-xs text-[var(--color-text-muted)]">
            {t('teamMeetings.board.count', { count: openPosts.length })}
          </span>
        </h2>
        <p className="text-xs text-[var(--color-text-muted)]">{t('teamMeetings.board.openHelp')}</p>
        <BoardViewport>
          <BoardCanvas layout={layout} renderZone={renderZone} />
        </BoardViewport>
      </section>

      <section className="space-y-2" aria-label={t('teamMeetings.board.triagedHeading')}>
        <h2 className="text-sm font-semibold text-[var(--color-text-primary)]">
          {t('teamMeetings.board.triagedHeading')}
          <span className="ml-1 text-xs text-[var(--color-text-muted)]">
            {t('teamMeetings.board.count', { count: triagedPosts.length })}
          </span>
        </h2>
        {triagedPosts.length === 0 ? (
          <p className="text-xs text-[var(--color-text-muted)]">
            {t('teamMeetings.board.triagedEmpty')}
          </p>
        ) : (
          <div className="grid grid-cols-2 items-start gap-4 rounded-lg border border-[#e4e2dc] bg-[#fcfbf7] p-4 sm:grid-cols-[repeat(auto-fill,minmax(160px,1fr))]">
            {triagedPosts.map(sticky)}
          </div>
        )}
      </section>

      {opened !== undefined && (
        <Modal
          open
          size="md"
          title={categoryLabel(opened.post.category)}
          onClose={() => {
            setOpenedId(null);
          }}
        >
          <PostCard
            view={opened}
            people={people}
            viewerId={viewerId}
            onTriage={handlersFor(opened.post)}
          />
        </Modal>
      )}
      {modalProps !== null && deciding?.kind === 'request' && (
        <ConvertToRequestModal {...modalProps} />
      )}
      {modalProps !== null && deciding?.kind === 'issue' && defaultBoard !== null && (
        <ConvertToIssueModal {...modalProps} board={defaultBoard} />
      )}
      {modalProps !== null && deciding?.kind === 'close' && <CloseWithNoteModal {...modalProps} />}
    </>
  );
}
